/**
 * 전송 모듈
 *
 * Navigator.sendBeacon / fetch / XMLHttpRequest를 사용하여 이벤트를 서버로 전송합니다.
 */

import type { QueuedEvent } from './queue';

export type TransportMethod = 'beacon' | 'fetch' | 'xhr';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export interface SendResult {
  success: boolean;
  retryable: boolean;
}

export class EventSender {
  private endpoint: string;
  private method: TransportMethod;
  private debug: boolean;

  constructor(endpoint: string, method: TransportMethod = 'beacon', debug: boolean = false) {
    this.endpoint = endpoint;
    this.method = method;
    this.debug = debug;
  }

  async send(events: QueuedEvent[]): Promise<SendResult> {
    if (events.length === 0) {
      return { success: true, retryable: false };
    }

    const payload = JSON.stringify({
      events,
      sent_at: Date.now(),
    });

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      const result = await this.trySend(payload);

      if (result.success) {
        if (this.debug) {
          this.log(`Sent ${events.length} events (attempt ${attempt + 1})`);
        }
        return result;
      }

      if (!result.retryable) {
        if (this.debug) {
          this.log(`Non-retryable failure sending ${events.length} events`);
        }
        return result;
      }

      if (attempt < MAX_RETRIES - 1) {
        await this.delay(RETRY_DELAY_MS * Math.pow(2, attempt));
      }
    }

    if (this.debug) {
      this.log(`Failed to send ${events.length} events after ${MAX_RETRIES} attempts`);
    }
    return { success: false, retryable: true };
  }

  sendSync(events: QueuedEvent[]): boolean {
    if (events.length === 0) return true;

    const payload = JSON.stringify({
      events,
      sent_at: Date.now(),
    });

    return this.sendViaBeacon(payload);
  }

  private async trySend(payload: string): Promise<SendResult> {
    switch (this.method) {
      case 'beacon': {
        const beaconSuccess = this.sendViaBeacon(payload);
        if (beaconSuccess) return { success: true, retryable: false };
        return this.sendViaFetch(payload);
      }
      case 'fetch':
        return this.sendViaFetch(payload);
      case 'xhr':
        return this.sendViaXhr(payload);
      default:
        return this.sendViaFetch(payload);
    }
  }

  private sendViaBeacon(payload: string): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return false;
    }

    try {
      const blob = new Blob([payload], { type: 'application/json' });
      return navigator.sendBeacon(this.endpoint, blob);
    } catch {
      return false;
    }
  }

  private async sendViaFetch(payload: string): Promise<SendResult> {
    if (typeof fetch === 'undefined') {
      return { success: false, retryable: false };
    }

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      });

      if (response.ok) {
        return { success: true, retryable: false };
      }

      const retryable = response.status >= 500;
      return { success: false, retryable };
    } catch {
      return { success: false, retryable: true };
    }
  }

  private sendViaXhr(payload: string): Promise<SendResult> {
    return new Promise((resolve) => {
      if (typeof XMLHttpRequest === 'undefined') {
        resolve({ success: false, retryable: false });
        return;
      }

      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', this.endpoint, true);
        xhr.setRequestHeader('Content-Type', 'application/json');

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ success: true, retryable: false });
          } else {
            resolve({ success: false, retryable: xhr.status >= 500 });
          }
        };

        xhr.onerror = () => {
          resolve({ success: false, retryable: true });
        };

        xhr.send(payload);
      } catch {
        resolve({ success: false, retryable: true });
      }
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private log(message: string): void {
    if (typeof console !== 'undefined') {
      console.debug(`[LambAnalytics] ${message}`);
    }
  }
}
